import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  apiRequest,
  getAuthToken,
  getAuthUser,
  setAuthUser,
  type AuthUser,
} from '../lib/api';
import { showToast } from '../components/Toast';
import { formatVnDate } from '../lib/dateUtils';

type ProfileResponse = {
  user: AuthUser & {
    createdAt: string;
  };
};

type MyDevice = {
  id: string;
  startAt: string;
  daysUntilAutoActivate?: number | null;
  device: {
    id: string;
    deviceUid: string;
    model: string;
    source: string;
    warrantyMonths: number;
    createdAt?: string;
    warrantyRecords: Array<{
      activatedAt: string;
      expiresAt: string;
      warrantyMonths?: number;
      activationReason?: string;
      activationNote?: string | null;
    }>;
  };
};

type MyDevicesResponse = { devices: MyDevice[] };

type EditForm = { name: string; phone: string; address: string };
type FormErrors = Partial<EditForm>;

function validateForm(form: EditForm): FormErrors {
  const errors: FormErrors = {};
  if (!form.name.trim()) {
    errors.name = 'Vui lòng nhập họ tên';
  } else if (form.name.trim().length < 2) {
    errors.name = 'Họ tên phải có ít nhất 2 ký tự';
  }
  if (form.phone && !/^(0|\+84)[0-9]{8,10}$/.test(form.phone.replace(/\s/g, ''))) {
    errors.phone = 'Số điện thoại không hợp lệ (VD: 0912345678)';
  }
  if (form.address && form.address.trim().length < 5) {
    errors.address = 'Địa chỉ phải có ít nhất 5 ký tự';
  }
  return errors;
}

function getWarrantyInfo(device: MyDevice): {
  label: string;
  status: 'valid' | 'auto' | 'expired' | 'pending';
  tooltip?: string;
} | null {
  const wr = device.device.warrantyRecords[0];

  if (!wr) {
    if (device.daysUntilAutoActivate !== undefined && device.daysUntilAutoActivate !== null && device.daysUntilAutoActivate > 0) {
      return {
        label: `⏳ Tự kích hoạt sau ${device.daysUntilAutoActivate} ngày`,
        status: 'pending',
        tooltip: `Thiết bị sẽ tự động kích hoạt bảo hành sau ${device.daysUntilAutoActivate} ngày nữa nếu bạn chưa đăng ký.`
      };
    }
    return null;
  }

  const expiry = new Date(wr.expiresAt);
  if (Number.isNaN(expiry.getTime())) return null;

  const now = new Date();
  const expired = now > expiry;
  const isAuto = wr.activationReason === 'AUTO_10_DAYS';

  const activatedDateStr = formatVnDate(wr.activatedAt);
  const expiryDateStr = formatVnDate(wr.expiresAt);
  const reasonStr = isAuto ? 'Tự động kích hoạt sau 10 ngày (tính từ xuất kho + 3 ngày)' : 'Kích hoạt thủ công';
  const tooltip = `Kích hoạt: ${activatedDateStr} (${reasonStr})\nHạn BH: ${expiryDateStr}${wr.activationNote ? `\nGhi chú: ${wr.activationNote}` : ''}`;

  if (expired) {
    return {
      label: '⚠️ Hết bảo hành',
      status: 'expired',
      tooltip,
    };
  }

  if (isAuto) {
    return {
      label: `🤖 Tự động (XK + 3 ngày) đến ${expiryDateStr}`,
      status: 'auto',
      tooltip,
    };
  }

  return {
    label: `✅ Bảo hành đến ${expiryDateStr}`,
    status: 'valid',
    tooltip,
  };
}

function getAvatarLabel(name: string, email: string): string {
  const source = name?.trim() || email || '';
  return (
    source
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0])
      .join('')
      .toUpperCase() || '?'
  );
}

export default function AccountPage() {
  const cachedAuth = getAuthUser();
  const [user, setUser] = useState<ProfileResponse['user'] | null>(
    cachedAuth ? {
      id: cachedAuth.id,
      email: cachedAuth.email,
      role: cachedAuth.role,
      name: cachedAuth.name,
      phone: cachedAuth.phone,
      address: cachedAuth.address,
      createdAt: cachedAuth.createdAt || new Date().toISOString(),
    } : null
  );
  const [devices, setDevices] = useState<MyDevice[]>([]);
  const [devicesLoading, setDevicesLoading] = useState(true);
  const [loading, setLoading] = useState(!cachedAuth);
  const [isEditing, setIsEditing] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [editForm, setEditForm] = useState<EditForm>({ name: '', phone: '', address: '' });
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  useEffect(() => {
    async function loadData() {
      const token = getAuthToken();
      if (!token) {
        setLoading(false);
        setDevicesLoading(false);
        showToast('Bạn chưa đăng nhập', 'error');
        return;
      }
      try {
        const profile = await apiRequest<ProfileResponse>('/auth/me', { method: 'GET' }, true);
        setUser(profile.user);
        setAuthUser(profile.user);
      } catch (error) {
        if (!cachedAuth) {
          showToast(error instanceof Error ? error.message : 'Không thể tải dữ liệu tài khoản', 'error');
        }
      } finally {
        setLoading(false);
      }

      // Load devices separately so profile shows fast
      try {
        const devRes = await apiRequest<MyDevicesResponse>('/device/my-devices', { method: 'GET' }, true);
        setDevices(devRes.devices ?? []);
      } catch {
        // silently fail – user just won't see devices
      } finally {
        setDevicesLoading(false);
      }
    }
    loadData();
  }, []);

  function startEditing() {
    setEditForm({
      name: user?.name || '',
      phone: user?.phone || '',
      address: user?.address || '',
    });
    setFormErrors({});
    setIsEditing(true);
  }

  async function handleUpdateProfile(e: React.FormEvent) {
    e.preventDefault();
    const errors = validateForm(editForm);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setUpdating(true);
    try {
      const result = await apiRequest<ProfileResponse>(
        '/auth/me',
        {
          method: 'PUT',
          body: JSON.stringify({
            name: editForm.name.trim() || undefined,
            phone: editForm.phone.trim() || undefined,
            address: editForm.address.trim() || undefined,
          }),
        },
        true
      );
      setUser(result.user);
      setAuthUser(result.user);
      setIsEditing(false);
      showToast('Cập nhật thông tin thành công!', 'success');
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Cập nhật thất bại', 'error');
    } finally {
      setUpdating(false);
    }
  }

  if (loading && !user) {
    return (
      <div className="page-block">
        <div className="section-heading" style={{ marginBottom: '28px' }}>
          <span className="eyebrow">Tài khoản</span>
          <h1 style={{ color: '#f8fafc' }}>Quản lý tài khoản cá nhân</h1>
        </div>
        <div className="account-layout">
          <aside className="profile-sidebar" style={{ minHeight: '480px', opacity: 0.6 }}>
            <div className="profile-sidebar-avatar">
              <div className="profile-sidebar-avatar-circle" style={{ background: 'rgba(255,255,255,0.08)' }} />
              <div style={{ height: '20px', width: '140px', background: 'rgba(255,255,255,0.08)', borderRadius: '6px', margin: '8px auto' }} />
            </div>
            <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {[1, 2, 3, 4].map(n => (
                <div key={n} style={{ height: '40px', background: 'rgba(255,255,255,0.04)', borderRadius: '10px' }} />
              ))}
            </div>
          </aside>
          <div className="account-panel-card" style={{ minHeight: '480px', opacity: 0.6 }} />
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="page-block">
        <div className="text-block">
          <p>Bạn chưa đăng nhập.</p>
          <p>Vui lòng đăng nhập để xem thông tin tài khoản.</p>
        </div>
      </div>
    );
  }

  const avatarLabel = getAvatarLabel(user.name || '', user.email || '');
  const displayName = user.name?.trim() || user.email || '';

  return (
    <div className="page-block">
      <div
        className="section-heading"
        style={{ marginBottom: '28px' }}
      >
        <span className="eyebrow">Tài khoản</span>
        <h1>Quản lý tài khoản cá nhân</h1>
      </div>

      <div className="account-layout">
        {/* ── Left: Profile Sidebar ── */}
        <aside className="profile-sidebar">
          {/* Avatar + name header */}
          <div className="profile-sidebar-avatar">
            <div className="profile-sidebar-avatar-circle" aria-hidden="true">
              {avatarLabel}
            </div>
            <div>
              <p className="profile-sidebar-name">{displayName}</p>
              {user.name?.trim() && (
                <p className="profile-sidebar-email">{user.email}</p>
              )}
            </div>
          </div>

          {/* Form fields maintain constant position & size */}
          <div className="profile-sidebar-content">
            <form id="profile-edit-form" className={`profile-edit-form-sidebar${isEditing ? ' is-editing' : ' is-viewing'}`} onSubmit={handleUpdateProfile} noValidate>
              <div className="form-field">
                <label>Họ tên <span className="required-mark">*</span></label>
                <input
                  type="text"
                  value={isEditing ? editForm.name : (user.name || '')}
                  placeholder={isEditing ? "Nhập họ tên" : "Chưa cập nhật"}
                  readOnly={!isEditing}
                  className={formErrors.name ? 'has-error' : ''}
                  onChange={(e) => {
                    setEditForm({ ...editForm, name: e.target.value });
                    if (formErrors.name) setFormErrors({ ...formErrors, name: undefined });
                  }}
                />
                {formErrors.name && <span className="field-error">⚠ {formErrors.name}</span>}
              </div>
              <div className="form-field">
                <label>Email</label>
                <input type="email" value={user.email} readOnly style={{ opacity: 0.7 }} />
              </div>
              <div className="form-field">
                <label>Số điện thoại</label>
                <input
                  type="tel"
                  value={isEditing ? editForm.phone : (user.phone || '')}
                  placeholder={isEditing ? "Nhập số điện thoại" : "Chưa cập nhật"}
                  readOnly={!isEditing}
                  className={formErrors.phone ? 'has-error' : ''}
                  onChange={(e) => {
                    setEditForm({ ...editForm, phone: e.target.value });
                    if (formErrors.phone) setFormErrors({ ...formErrors, phone: undefined });
                  }}
                />
                {formErrors.phone && <span className="field-error">⚠ {formErrors.phone}</span>}
              </div>
              <div className="form-field">
                <label>Địa chỉ</label>
                <input
                  type="text"
                  value={isEditing ? editForm.address : (user.address || '')}
                  placeholder={isEditing ? "Nhập địa chỉ" : "Chưa cập nhật"}
                  readOnly={!isEditing}
                  className={formErrors.address ? 'has-error' : ''}
                  onChange={(e) => {
                    setEditForm({ ...editForm, address: e.target.value });
                    if (formErrors.address) setFormErrors({ ...formErrors, address: undefined });
                  }}
                />
                {formErrors.address && <span className="field-error">⚠ {formErrors.address}</span>}
              </div>
              <div className="form-field">
                <label>Vai trò</label>
                <input type="text" value={user.role === 'ADMIN' ? 'Admin' : 'Người dùng'} readOnly style={{ opacity: 0.7 }} />
              </div>
              <div className="form-field">
                <label>Thành viên từ</label>
                <input type="text" value={formatVnDate(user.createdAt)} readOnly style={{ opacity: 0.7 }} />
              </div>
            </form>
          </div>

          {/* Action buttons permanently pinned at bottom to avoid any layout shifts */}
          <div className="profile-sidebar-actions">
            {!isEditing ? (
              <button type="button" className="profile-edit-btn" onClick={startEditing}>
                ✏️ Chỉnh sửa thông tin
              </button>
            ) : (
              <div className="profile-edit-actions-row">
                <button
                  type="button"
                  className="button outline"
                  onClick={() => { setIsEditing(false); setFormErrors({}); }}
                  disabled={updating}
                  style={{ flex: 1 }}
                >
                  Hủy
                </button>
                <button
                  type="submit"
                  form="profile-edit-form"
                  className="button primary"
                  disabled={updating}
                  style={{ flex: 2 }}
                >
                  {updating ? '⏳ Đang lưu...' : '💾 Lưu thay đổi'}
                </button>
              </div>
            )}
          </div>
        </aside>

        {/* ── Right: BMCU Devices Panel ── */}
        <motion.div
          className="account-right-panel"
          initial={{ opacity: 0, x: 25 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1], delay: 0.08 }}
        >
          <div className="account-panel-card">
            <div className="account-panel-header">
              <h2 className="account-panel-title"><span className="bmcu-mark" aria-hidden="true">B</span>Thiết bị BMCU của tôi</h2>
              <span className="account-panel-count">{devicesLoading ? '…' : devices.length}</span>
            </div>

            {devicesLoading ? (
              <div className="device-empty">
                <div style={{ fontSize: '2rem', marginBottom: 8 }}>⏳</div>
                <p style={{ color: '#94a3b8' }}>Đang tải danh sách thiết bị...</p>
              </div>
            ) : devices.length === 0 ? (
              <div className="device-empty">
                <div className="device-empty-icon">📦</div>
                <p className="device-empty-text">Chưa có thiết bị nào</p>
                <p className="device-empty-sub">
                  Kích hoạt BMCU của bạn tại trang <strong>BMCU Center</strong> để quản lý bảo hành và lịch sử.
                </p>
              </div>
            ) : (
              <div className="device-list">
                {devices.map((d, index) => {
                  const warranty = getWarrantyInfo(d);
                  return (
                    <motion.div
                      key={d.id}
                      className="device-item"
                      initial={{ opacity: 0, y: 12 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, amount: 0.2 }}
                      transition={{ duration: 0.35, delay: index * 0.05 }}
                    >
                      <div className="device-icon" aria-hidden="true"><span>B</span></div>
                      <div className="device-info">
                        <p className="device-uid">{d.device.deviceUid}</p>
                        <div className="device-meta">
                          {d.device.model && (
                            <span className="device-badge model">{d.device.model}</span>
                          )}
                          {warranty && (
                            <span
                              className={`device-badge warranty ${warranty.status}`}
                              title={warranty.tooltip}
                            >
                              {warranty.label}
                            </span>
                          )}
                          <span>
                            Kích hoạt: {formatVnDate(d.startAt)}
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
